import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import { SIDEBAR_PARENT_MENUS } from "@/lib/constants/sidebarConfig";

export function useSidebarMenu() {
  const store = useStore();
  const router = useRouter();
  const { t } = useI18n();

  const conditions = computed(() => ({
    isStaging: store.getters.isStaging,
    isNotOnPrem: !store.getters.isOnPrem,
    hasDetectionApiAccess: store.getters.hasDetectionApiAccess,
    notStaging: !store.getters.isStaging,
  }));

  const evaluateVisibility = (visibleWhen) => {
    if (!visibleWhen || visibleWhen.length === 0) {
      return true;
    }
    return visibleWhen.every((condition) => conditions.value[condition]);
  };

  const checkProductAccess = (productGroup) => {
    if (!productGroup) {
      return true;
    }
    return store.getters.hasAccessToProductGroup(productGroup);
  };

  const resolveText = (sidebarMeta) => {
    if (sidebarMeta.text) {
      return sidebarMeta.text;
    }
    return sidebarMeta.i18nKey ? t(sidebarMeta.i18nKey) : "";
  };

  const sidebarList = computed(() => {
    const allRoutes = router.getRoutes();
    const sidebarRoutes = allRoutes.filter((r) => r.meta?.sidebar);

    const topLevel = [];
    const childrenByParent = {};

    sidebarRoutes.forEach((r) => {
      const { sidebar } = r.meta;

      if (!evaluateVisibility(sidebar.visibleWhen)) {
        return;
      }

      if (!checkProductAccess(sidebar.productGroup)) {
        return;
      }

      const item = {
        id: r.name,
        text: resolveText(sidebar),
        icon: sidebar.icon,
        routerLink: r.name,
        sortOrder: sidebar.sortOrder,
      };

      if (sidebar.parentId) {
        if (!childrenByParent[sidebar.parentId]) {
          childrenByParent[sidebar.parentId] = [];
        }
        childrenByParent[sidebar.parentId].push(item);
      } else {
        topLevel.push(item);
      }
    });

    Object.keys(SIDEBAR_PARENT_MENUS).forEach((parentId) => {
      const children = childrenByParent[parentId];
      if (!children || children.length === 0) {
        return;
      }

      const parentDef = SIDEBAR_PARENT_MENUS[parentId];

      if (!evaluateVisibility(parentDef.visibleWhen)) {
        return;
      }

      topLevel.push({
        id: parentId,
        text: t(parentDef.i18nKey),
        icon: parentDef.icon,
        sortOrder: parentDef.sortOrder,
        isParent: true,
        children: children.sort((a, b) => a.sortOrder - b.sortOrder),
      });
    });

    return topLevel.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  return { sidebarList };
}
